import React from "react";
import { useFetchLocalStorage } from "../../lib/useFetchLocalStorage";
import styled from "styled-components";
import { useRouter } from "next/router";
import Joi from "@hapi/joi";
import useUser from "../../lib/useUser";
import { Col, Typography, Divider, Button } from "antd";
import { useForm, useFieldArray } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers";

import useJobTypeData from "hooks/api/job/jobType/useJobTypeData";
import useFinishingData from "hooks/api/job/finishing/useFinishingData";
import useBindingData from "hooks/api/job/binding/useBindingData";
import AddFinishingModal from "components/modal/AddFinishingModal";
import AddBindingModal from "components/modal/AddBindingModal";
import AddPaperModal from "components/modal/AddPaperModal";
import { NotificationStateContext } from "context/UI/Notification/NotificationState";
import { apiAxios } from "services/HttpServices";
import AddOrEditJobForm from "components/job/shared/AddOrEditJobForm";

const { Text, Title } = Typography;

let hotStamping = Joi.object().keys({
  color: Joi.string().required(),
});

let selectedColor = Joi.object().keys({
  type: Joi.string().valid("CMYK", "PANTONE").required(),
  color: Joi.string().required(),
});

let printColor = Joi.object().keys({
  type: Joi.string().valid("1 Color", "2 Color", "4 Color").required(),
  selected_color: Joi.array()
    .when("type", {
      is: "1 Color",
      then: Joi.array().min(1).max(1).items(selectedColor).required(),
    })
    .when("type", {
      is: "2 Color",
      then: Joi.array().min(2).max(2).items(selectedColor).required(),
    })
    .when("type", {
      is: "4 Color",
      then: Joi.array().min(4).max(4).items(selectedColor).required(),
    }),
});

const secondOption = Joi.object().keys({
  name: Joi.string(),
  value: Joi.string(),
});
const selectedBinding = Joi.object().keys({
  name: Joi.string().required(),
  second_option: Joi.array().items(secondOption),
});

const jobSchema = Joi.object({
  name: Joi.string().required(),
  quantity: Joi.number().required(),
  delivery_date: Joi.string().allow(""),
  remarks: Joi.string().allow(null),
  job_type_id: Joi.number().required(),
  client_id: Joi.number().required(),
  finishings: Joi.array().min(1).required(),
  bindings: Joi.array().min(1).required(),
  general: Joi.object({
    orientation: Joi.string().required(),
    size: Joi.string().required(),
    pages: Joi.number().required(),
    urgent: Joi.boolean(),
  }),
  cover: Joi.object({
    print_color: Joi.array().items(printColor).required(),
    paper_material_id: Joi.number().required("Paper material required"),
    hot_stamping: Joi.array().items(hotStamping),
  }).unknown(),
  content: Joi.object({
    print_color: Joi.array().items(printColor).required(),
    paper_material_id: Joi.number().required(),
  }).unknown(),
  selected_binding: Joi.array().items(selectedBinding).required(),
});

const Add = () => {
  useFetchLocalStorage();
  useUser({ redirectTo: "/login" });

  // State
  const [finishingModalVisible, setFinishingModalVisible] = React.useState(
    false
  );
  const [bindingModalVisible, setBindingModalVisible] = React.useState(false);
  const [paperModalVisible, setPaperModalVisible] = React.useState(false);
  const [addPaperGrammageFrom, setAddPaperGrammageFrom] = React.useState(null);
  const [showLogs, setShowLogs] = React.useState(false);
  const [bookSizeUnit, setBookSizeUnit] = React.useState("INCI");

  // Use data hook
  const { data: jobTypeData, status: jobTypeStatus } = useJobTypeData();
  const { data: finishingData, status: finishingStatus } = useFinishingData();
  const { data: bindingData, status: bindingStatus } = useBindingData();
  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    errors,
    reset,
    watch,
    getValues,
  } = useForm({
    resolver: joiResolver(jobSchema),
    defaultValues: {
      name: null,
      quantity: null,
      delivery_date: "",
      bapak_kau: "power",
      remarks: null,
      job_type_id: null,
      client_id: null,
      general: {
        orientation: "Portrait",
        size: "",
        cover_type: null,
        pages: null,
        urgent: false,
      },
      cover: {
        print_color: [
          {
            type: "1 Color",
            selected_color: [
              {
                type: "CMYK",
                color: "BLACK",
              },
            ],
          },
        ],
        hot_stamping: [],
        paper_material_id: null,
        paper_grammage: null,
      },
      content: {
        print_color: [
          {
            type: "1 Color",
            selected_color: [
              {
                type: "CMYK",
                color: "BLACK",
              },
            ],
          },
        ],
        paper_material_id: null,
        paper_grammage: null,
      },
      bindings: [],
      finishings: [],
      selected_binding: [],
    },
  });

  const { fields: coverFields } = useFieldArray({
    control,
    name: "cover.print_color",
  });

  const { fields: contentFields } = useFieldArray({
    control,
    name: "content.print_color",
  });

  // State
  const [error, setError] = React.useState(null);
  const [newFinishingId, setNewFinishingId] = React.useState(null);

  //Form watch
  const watchFinishings = watch("finishings", []);
  const watchBindings = watch("bindings");
  // console.log("watchCover", watchCover);
  // Use Effect
  React.useEffect(() => {
    if (newFinishingId !== null) {
      setValue("finishings", [newFinishingId, ...watchFinishings]);
      setNewFinishingId(null);
    }
  }, [newFinishingId]);

  // Global State
  const {
    state: notificationState,
    setSuccessMessage,
    clearSuccessMessage,
    setLoading,
    finishLoading,
  } = React.useContext(NotificationStateContext);

  // Router
  const router = useRouter();

  const handleCloseFinishingModal = () => {
    setFinishingModalVisible(false);
  };

  const onSubmit = async (data) => {
    setError(null);
    setLoading("createJob");
    data.general.size = `${data.general.size} ${bookSizeUnit}`;
    const newBindings = data.bindings.map((binding) => ({ id: binding }));
    const newFinishings = data.finishings.map((finishing) => ({
      id: finishing,
    }));
    const dataToSubmit = {
      ...data,
      bindings: newBindings,
      finishings: newFinishings,
    };
    const cleanData = _.pickBy(dataToSubmit, _.identity);
    console.log("cleanData", cleanData);

    try {
      const newJob = await apiAxios.post("/jobs", cleanData);
      finishLoading("createJob");
      setSuccessMessage("Succesfully created job");
      router.push(`/jobs/[id]`, `/jobs/${newJob.data.data.id}`);
      clearSuccessMessage();
      reset();
    } catch (e) {
      finishLoading("createJob");
      setError(e.response.data.message);
    }
  };

  return (
    <>
      <Col span={24}>
        <Title level={4}>Add New Job</Title>
      </Col>

      {showLogs && (
        <div>
          <div style={{ margin: "1rem 0" }}>
            <pre
              style={{
                background: "#000000",
                color: "#26f704",
                fontSize: ".85rem",
                padding: ".5rem",
              }}
            >
              <strong>props</strong> = {JSON.stringify(watch(), null, 2)}
            </pre>
          </div>

          <div style={{ margin: "1rem 0" }}>
            <pre
              style={{
                background: "#000000",
                color: "#26f704",
                fontSize: ".85rem",
                padding: ".5rem",
              }}
            >
              <strong>Selected Binding</strong> =
              {JSON.stringify(watch("selected_binding"), null, 2)}
            </pre>
          </div>

          <div style={{ margin: "1rem 0" }}>
            <pre
              style={{
                background: "#000000",
                color: "#f70739",
                fontSize: ".85rem",
                padding: ".5rem",
              }}
            >
              <strong>Errors</strong> ={JSON.stringify(errors, null, 2)}
            </pre>
          </div>
        </div>
      )}

      <FancyButtonDiv>
        <button
          className="glow-on-hover"
          onClick={() => setShowLogs(!showLogs)}
        >
          {showLogs ? "Hide" : "Show"} Logs
        </button>
      </FancyButtonDiv>
      {error && (
        <ErrorContainer>
          <p>{error}</p>
        </ErrorContainer>
      )}

      <form key={1} onSubmit={handleSubmit(onSubmit)}>
        <AddOrEditJobForm
          control={control}
          errors={errors}
          getValues={getValues}
          setValue={setValue}
          trigger={trigger}
          watch={watch}
          reset={reset}
          jobTypeData={jobTypeData}
          jobTypeStatus={jobTypeStatus}
          finishingData={finishingData}
          finishingStatus={finishingStatus}
          bindingData={bindingData}
          bindingStatus={bindingStatus}
          setFinishingModalVisible={setFinishingModalVisible}
          setBindingModalVisible={setBindingModalVisible}
          setPaperModalVisible={setPaperModalVisible}
          setAddPaperGrammageFrom={setAddPaperGrammageFrom}
          watchBindings={watchBindings}
          coverFields={coverFields}
          contentFields={contentFields}
          bookSizeUnit={bookSizeUnit}
          setBookSizeUnit={setBookSizeUnit}
        />
        <ButtonContainer>
          <Button
            htmlType="submit"
            disabled={notificationState.loading["createJob"]}
            loading={notificationState.loading["createJob"]}
            type="primary"
          >
            {notificationState.loading["createJob"]
              ? "Creating Job..."
              : "Create Job"}
          </Button>
        </ButtonContainer>
      </form>
      <AddFinishingModal
        visible={finishingModalVisible}
        handleClose={handleCloseFinishingModal}
        setNewFinishingId={setNewFinishingId}
      />
      <AddBindingModal
        visible={bindingModalVisible}
        setBindingModalVisible={setBindingModalVisible}
      />
      <AddPaperModal
        visible={paperModalVisible}
        setValue={setValue}
        setPaperModalVisible={setPaperModalVisible}
        addPaperGrammageFrom={addPaperGrammageFrom}
      />
    </>
  );
};

const ErrorContainer = styled.div`
  padding: ${(props) => props.theme.spacing[4]};
  border: 1px solid ${(props) => props.theme.colors.red[700]};

  background-color: ${(props) => props.theme.colors.red[200]};
  margin-top: ${(props) => props.theme.spacing[2]};
  p {
    color: ${(props) => props.theme.colors.red[900]};
    font-size: ${(props) => props.theme.fontSize["small"]};
  }
`;

export const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${(props) => props.theme.spacing[3]};
  padding-right: ${(props) => props.theme.spacing[3]};
`;
export const StyledTitle = styled(Text)`
  font-weight: 500;
  color: ${(props) => props.theme.colors.grey[600]};
  margin-bottom: ${(props) => props.theme.spacing[1]};
`;

export const StyledDivider = styled(Divider)`
  border-top: 1px solid ${(props) => props.theme.colors.grey[400]};
  margin: ${(props) => props.theme.spacing[3]} 0;
`;

const FancyButtonDiv = styled.div`
  .glow-on-hover {
    width: 90px;
    height: 30px;
    border: none;
    outline: none;
    color: #fff;
    background: #111;
    cursor: pointer;
    position: relative;
    z-index: 0;
    border-radius: 9999px;
    padding: 0;
    font-size: ${(props) => props.theme.fontSize["xs"]};
  }

  .glow-on-hover:before {
    content: "";
    background: linear-gradient(
      45deg,
      #ff0000,
      #ff7300,
      #fffb00,
      #48ff00,
      #00ffd5,
      #002bff,
      #7a00ff,
      #ff00c8,
      #ff0000
    );
    position: absolute;
    top: -2px;
    left: -2px;
    background-size: 400%;
    z-index: -1;
    filter: blur(5px);
    width: calc(100% + 4px);
    height: calc(100% + 4px);
    animation: glowing 20s linear infinite;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
    border-radius: 10px;
  }

  .glow-on-hover:active {
    color: #000;
  }

  .glow-on-hover:active:after {
    background: transparent;
  }

  .glow-on-hover:hover:before {
    opacity: 1;
  }

  .glow-on-hover:after {
    z-index: -1;
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    background: #111;
    left: 0;
    top: 0;
    border-radius: 10px;
  }

  @keyframes glowing {
    0% {
      background-position: 0 0;
    }
    50% {
      background-position: 400% 0;
    }
    100% {
      background-position: 0 0;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export default Add;
